import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Logo, { LogoMark } from "@/components/Logo";
import { MaskedLine } from "@/components/Reveal";
import { track } from "@/lib/track";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRAFT_KEY = "hv_quote_draft";
const STEP_KEY = "hv_quote_step";
const EMPTY_DATA = {
  event_type: "",
  event_date: "",
  start_time: "",
  date_flexible: false,
  location: "",
  city_zip: "",
  guest_count: "",
  desired_service: "",
  duration: "",
  bar_onsite: "",
  setting: "",
  enhancements: [],
  details: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
};
const loadDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT_KEY)) || null;
  } catch {
    return null;
  }
};
const TOTAL_STEPS = 11;

const inputCls =
  "w-full border-b border-gold/25 bg-transparent py-4 font-sans text-base font-light text-ivory outline-none transition-colors duration-300 placeholder:text-ivory/50 focus:border-gold";

const OptionCard = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-testid={testid}
    className={`border px-6 py-5 text-left font-sans text-sm font-light tracking-wide transition-colors duration-300 ${
      selected
        ? "border-gold bg-gold text-midnight"
        : "border-gold/25 text-ivory/80 hover:border-gold/70 hover:text-ivory"
    }`}
  >
    {children}
  </button>
);

const StepShell = ({ title, hint, children }) => (
  <div>
    <h2 className="font-serif text-3xl font-light leading-tight text-ivory md:text-4xl">
      {title}
    </h2>
    {hint && (
      <p className="mt-3 font-sans text-sm font-light text-ivory/60">{hint}</p>
    )}
    <div className="mt-10">{children}</div>
  </div>
);

const slug = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const labelOf = (list, v) => list.find((o) => o.value === v)?.label || v;

const QuotePage = () => {
  const { lang, t } = useLang();
  const q = t.quote;
  const [step, setStep] = useState(() => Number(sessionStorage.getItem(STEP_KEY)) || 0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [refId, setRefId] = useState("");
  const [data, setData] = useState(() => ({ ...EMPTY_DATA, ...(loadDraft() || {}) }));

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(data));
    sessionStorage.setItem(STEP_KEY, String(step));
  }, [data, step]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleEnhancement = (s) => {
    const EXCLUSIVE = ["Not Sure Yet", "None"];
    const cur = data.enhancements;
    if (EXCLUSIVE.includes(s)) {
      set("enhancements", cur.includes(s) ? [] : [s]);
      return;
    }
    const next = cur.filter((x) => !EXCLUSIVE.includes(x));
    set("enhancements", next.includes(s) ? next.filter((x) => x !== s) : [...next, s]);
  };

  const canContinue = () => {
    switch (step) {
      case 0: return !!data.event_type;
      case 1: return !!data.event_date || data.date_flexible;
      case 2: return data.location.trim().length > 1;
      case 3: return !!data.guest_count;
      case 4: return !!data.desired_service;
      case 5: return !!data.duration;
      case 6: return !!data.bar_onsite && !!data.setting;
      case 7: return true;
      case 8: return true;
      case 9:
        return (
          data.first_name.trim().length > 0 &&
          data.last_name.trim().length > 0 &&
          /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)
        );
      default: return true;
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        name: `${data.first_name} ${data.last_name}`.trim(),
        preferred_language: lang,
      };
      const res = await axios.post(`${API}/inquiries`, payload);
      setRefId((res.data.id || "").slice(0, 8).toUpperCase());
      setDone(true);
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(STEP_KEY);
      track("quote_submitted", { event_type: data.event_type, guests: data.guest_count, lang });
      window.__lenis?.scrollTo(0, { immediate: true });
    } catch (e) {
      toast.error(q.error);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    <StepShell key="s0" title={q.s0.title} hint={q.s0.hint}>
      <div className="grid gap-4 sm:grid-cols-2" data-testid="quote-step-event-type">
        {q.eventTypes.map((o) => (
          <OptionCard
            key={o.value}
            selected={data.event_type === o.value}
            onClick={() => set("event_type", o.value)}
            testid={`quote-event-type-${slug(o.value)}`}
          >
            {o.label}
          </OptionCard>
        ))}
      </div>
    </StepShell>,

    <StepShell key="s1" title={q.s1.title} hint={q.s1.hint}>
      <p className="font-sans text-[10px] tracking-[0.35em] text-gold/70">{q.s1.dateLabel}</p>
      <input
        type="date"
        value={data.event_date}
        onChange={(e) => set("event_date", e.target.value)}
        aria-label={q.s1.dateLabel}
        data-testid="quote-event-date-input"
        className={`${inputCls} mt-2 max-w-xs`}
      />
      <p className="mt-8 font-sans text-[10px] tracking-[0.35em] text-gold/70">
        {q.s1.timeLabel} <span className="text-ivory/50">{q.s1.optional}</span>
      </p>
      <input
        type="time"
        value={data.start_time}
        onChange={(e) => set("start_time", e.target.value)}
        data-testid="quote-start-time-input"
        aria-label={`${q.s1.timeLabel} ${q.s1.optional}`}
        className={`${inputCls} mt-2 max-w-xs`}
      />
      <label className="mt-6 flex cursor-pointer items-center gap-3" data-testid="quote-date-flexible">
        <input
          type="checkbox"
          checked={data.date_flexible}
          onChange={(e) => set("date_flexible", e.target.checked)}
          className="h-4 w-4 accent-[#C8A46A]"
        />
        <span className="font-sans text-sm font-light text-ivory/70">{q.s1.flexible}</span>
      </label>
    </StepShell>,

    <StepShell key="s2" title={q.s2.title} hint={q.s2.hint}>
      <div className="flex flex-col gap-8">
        <input
          type="text"
          value={data.location}
          onChange={(e) => set("location", e.target.value)}
          placeholder={q.s2.locationPh}
          data-testid="quote-location-input"
          className={inputCls}
        />
        <input
          type="text"
          value={data.city_zip}
          onChange={(e) => set("city_zip", e.target.value)}
          placeholder={q.s2.zipPh}
          data-testid="quote-city-zip-input"
          className={inputCls}
        />
      </div>
    </StepShell>,

    <StepShell key="s3" title={q.s3.title}>
      <div className="flex flex-wrap gap-4" data-testid="quote-step-guest-count">
        {q.guestCounts.map((o) => (
          <OptionCard
            key={o.value}
            selected={data.guest_count === o.value}
            onClick={() => set("guest_count", o.value)}
            testid={`quote-guests-${slug(o.value)}`}
          >
            {o.label}
          </OptionCard>
        ))}
      </div>
    </StepShell>,

    <StepShell key="s4" title={q.s4.title} hint={q.s4.hint}>
      <div className="flex flex-col gap-4" data-testid="quote-step-service">
        {q.services.map((s) => (
          <OptionCard
            key={s.value}
            selected={data.desired_service === s.value}
            onClick={() => {
              setData((d) => ({
                ...d,
                desired_service: s.value,
                enhancements:
                  s.value === "Hudson & Stir Experience"
                    ? d.enhancements.filter((x) => x !== "Portable Bar")
                    : d.enhancements,
              }));
            }}
            testid={`quote-service-${slug(s.value)}`}
          >
            <span className="flex items-baseline justify-between gap-4">
              <span>{s.label}</span>
              <span className={`font-sans text-[11px] tracking-[0.15em] ${data.desired_service === s.value ? "text-midnight/70" : "text-gold/70"}`}>
                {s.price}
              </span>
            </span>
          </OptionCard>
        ))}
      </div>
    </StepShell>,

    <StepShell key="s5" title={q.s5.title} hint={q.s5.hint}>
      <div className="grid gap-4 sm:grid-cols-2" data-testid="quote-step-duration">
        {q.durations.map((o) => (
          <OptionCard
            key={o.value}
            selected={data.duration === o.value}
            onClick={() => set("duration", o.value)}
            testid={`quote-duration-${slug(o.value)}`}
          >
            {o.label}
          </OptionCard>
        ))}
      </div>
    </StepShell>,

    <StepShell key="s6" title={q.s6.title} hint={q.s6.hint}>
      <p className="font-sans text-[10px] tracking-[0.35em] text-gold/70">{q.s6.barLabel}</p>
      <div className="mt-4 flex flex-wrap gap-3" data-testid="quote-step-bar-onsite">
        {q.s6.barOptions.map((o) => (
          <OptionCard
            key={o.value}
            selected={data.bar_onsite === o.value}
            onClick={() => set("bar_onsite", o.value)}
            testid={`quote-bar-${slug(o.value)}`}
          >
            {o.label}
          </OptionCard>
        ))}
      </div>
      <p className="mt-10 font-sans text-[10px] tracking-[0.35em] text-gold/70">{q.s6.settingLabel}</p>
      <div className="mt-4 flex flex-wrap gap-3" data-testid="quote-step-setting">
        {q.s6.settingOptions.map((o) => (
          <OptionCard
            key={o.value}
            selected={data.setting === o.value}
            onClick={() => set("setting", o.value)}
            testid={`quote-setting-${slug(o.value)}`}
          >
            {o.label}
          </OptionCard>
        ))}
      </div>
    </StepShell>,

    <StepShell key="s7" title={q.s7.title} hint={q.s7.hint}>
      <div className="flex flex-wrap gap-3" data-testid="quote-step-enhancements">
        {q.enhancements.map((o) => {
          const includedWithService = o.value === "Portable Bar" && data.desired_service === "Hudson & Stir Experience";
          if (includedWithService) {
            return (
              <span
                key={o.value}
                data-testid="quote-enhancement-portable-bar-included"
                aria-disabled="true"
                className="border border-gold/40 px-5 py-3 font-sans text-xs tracking-[0.15em] text-gold/80"
              >
                {q.s7.includedNote}
              </span>
            );
          }
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggleEnhancement(o.value)}
              aria-pressed={data.enhancements.includes(o.value)}
              data-testid={`quote-enhancement-${slug(o.value)}`}
              className={`border px-5 py-3 font-sans text-xs tracking-[0.15em] transition-colors duration-300 ${
                data.enhancements.includes(o.value)
                  ? "border-gold bg-gold text-midnight"
                  : "border-gold/25 text-ivory/75 hover:border-gold/70"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </StepShell>,

    <StepShell key="s8" title={q.s8.title} hint={q.s8.hint}>
      <p className="mb-4 font-sans text-[10px] tracking-[0.35em] text-gold/70">{q.s8.optional}</p>
      <textarea
        rows={6}
        value={data.details}
        onChange={(e) => set("details", e.target.value)}
        placeholder={q.s8.placeholder}
        data-testid="quote-details-textarea"
        className={`${inputCls} resize-none border border-gold/25 p-5 focus:border-gold`}
      />
    </StepShell>,

    <StepShell key="s9" title={q.s9.title}>
      <div className="flex flex-col gap-8">
        <div className="grid gap-8 sm:grid-cols-2">
          <input
            type="text"
            value={data.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            placeholder={q.s9.firstPh}
            data-testid="quote-first-name-input"
            className={inputCls}
          />
          <input
            type="text"
            value={data.last_name}
            onChange={(e) => set("last_name", e.target.value)}
            placeholder={q.s9.lastPh}
            data-testid="quote-last-name-input"
            className={inputCls}
          />
        </div>
        <input
          type="email"
          value={data.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder={q.s9.emailPh}
          data-testid="quote-email-input"
          className={inputCls}
        />
        <input
          type="tel"
          value={data.phone}
          onChange={(e) => set("phone", e.target.value)}
          placeholder={q.s9.phonePh}
          data-testid="quote-phone-input"
          className={inputCls}
        />
        {(data.first_name || data.last_name || data.email) && !canContinue() && (
          <p
            data-testid="quote-contact-hint"
            className="font-sans text-xs font-light tracking-wide text-gold/70"
          >
            {q.s9.hint}
          </p>
        )}
      </div>
    </StepShell>,

    <StepShell key="s10" title={q.s10.title} hint={q.s10.hint}>
      <dl className="flex flex-col divide-y divide-gold/15 border-y border-gold/15" data-testid="quote-review-summary">
        {[
          [q.s10.rows.event, labelOf(q.eventTypes, data.event_type), 0, "event"],
          [q.s10.rows.date, `${data.date_flexible ? `${data.event_date || "—"} ${q.s10.flexibleSuffix}` : data.event_date || "—"}${data.start_time ? ` · ${data.start_time}` : ""}`, 1, "date"],
          [q.s10.rows.location, [data.location, data.city_zip].filter(Boolean).join(", "), 2, "location"],
          [q.s10.rows.guests, labelOf(q.guestCounts, data.guest_count), 3, "guests"],
          [q.s10.rows.service, labelOf(q.services, data.desired_service), 4, "service"],
          [q.s10.rows.duration, labelOf(q.durations, data.duration), 5, "duration"],
          [q.s10.rows.venue, [data.bar_onsite && `${q.s10.barPrefix} ${labelOf(q.s6.barOptions, data.bar_onsite)}`, labelOf(q.s6.settingOptions, data.setting)].filter(Boolean).join(q.venueJoin), 6, "venue"],
          [q.s10.rows.extras, data.enhancements.filter((x) => !(x === "Portable Bar" && data.desired_service === "Hudson & Stir Experience")).map((x) => labelOf(q.enhancements, x)).join(", "), 7, "extras"],
          [q.s10.rows.name, `${data.first_name} ${data.last_name}`.trim(), 9, "name"],
          [q.s10.rows.email, data.email, 9, "email"],
          [q.s10.rows.phone, data.phone || "—", 9, "phone"],
        ].map(([k, v, s, id]) => (
          <div key={id} className="flex items-baseline gap-6 py-4">
            <dt className="w-24 shrink-0 pt-1 font-sans text-[10px] uppercase tracking-[0.3em] text-gold/80">{k}</dt>
            <dd className="font-sans text-sm font-light text-ivory/85">{v || "—"}</dd>
            <button
              type="button"
              onClick={() => setStep(s)}
              data-testid={`quote-edit-${id}`}
              className="ml-auto shrink-0 p-1 font-sans text-[10px] tracking-[0.25em] text-gold/60 transition-colors duration-300 hover:text-gold"
            >
              {q.s10.edit}
            </button>
          </div>
        ))}
      </dl>
      <p className="mt-6 font-sans text-xs font-light leading-relaxed text-ivory/55">
        {q.s10.note}
      </p>
      <p className="mt-3 font-sans text-xs font-light leading-relaxed text-ivory/45">
        {q.s10.privacyPre}{" "}
        <Link
          to="/privacy"
          data-testid="quote-privacy-link"
          className="text-gold/80 underline underline-offset-4 transition-colors duration-300 hover:text-gold"
        >
          {q.s10.privacyLink}
        </Link>
      </p>
    </StepShell>,
  ];

  if (done) {
    return (
      <main data-testid="quote-confirmation" className="hv-grain flex min-h-screen flex-col items-center justify-center bg-midnight px-6 py-32 text-center">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
          <LogoMark size={64} className="mx-auto" />
        </motion.div>
        <h1 className="mt-10 font-serif text-5xl font-light tracking-wide text-ivory md:text-7xl">
          <MaskedLine delay={0.2}>{q.confirm.titlePre}<span className="italic text-gold">{q.confirm.titleEm}</span></MaskedLine>
        </h1>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }}>
          <p className="mt-8 font-sans text-base font-light text-ivory/75">
            {q.confirm.line1}
          </p>
          <p className="mt-3 max-w-md font-sans text-sm font-light leading-relaxed text-ivory/50">
            {q.confirm.line2}
          </p>
          {refId && (
            <p className="mt-8 font-sans text-[10px] tracking-[0.4em] text-gold/70" data-testid="quote-reference-id">
              {q.confirm.ref} {refId}
            </p>
          )}
          <div className="mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link to="/" data-testid="confirmation-return-home-btn" className="bg-gold px-10 py-4 font-sans text-[11px] tracking-[0.3em] text-midnight transition-colors duration-300 hover:bg-gold-light">
              {q.confirm.home}
            </Link>
            <Link to="/gallery" data-testid="confirmation-view-gallery-btn" className="border border-ivory/30 px-10 py-4 font-sans text-[11px] tracking-[0.3em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold">
              {q.confirm.gallery}
            </Link>
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="quote-page" className="bg-midnight">
      <div className="mx-auto grid max-w-[1680px] gap-16 px-6 pb-32 pt-32 lg:grid-cols-12 lg:px-12 lg:pt-40">
        <aside className="hidden lg:col-span-4 lg:block">
          <div className="sticky top-32">
            <Logo variant="hero" />
            <div className="mt-14 h-px w-16 bg-gold/50" />
            <p className="mt-8 max-w-xs font-serif text-2xl font-light italic leading-relaxed text-ivory/70">
              {q.sidebar.quote}
            </p>
            <p className="mt-8 max-w-xs font-sans text-xs font-light leading-relaxed text-ivory/40">
              {q.sidebar.body}
            </p>
          </div>
        </aside>

        <div className="lg:col-span-8 lg:pl-8">
          <div className="mb-12" data-testid="quote-progress">
            <div className="flex items-baseline justify-between">
              <span className="font-sans text-[10px] tracking-[0.4em] text-gold">
                {q.stepWord} {String(step + 1).padStart(2, "0")} <span className="text-ivory/40">/ {TOTAL_STEPS}</span>
              </span>
              <span className="font-sans text-[10px] tracking-[0.3em] text-ivory/40">
                {q.stepLabels[step]}
              </span>
            </div>
            <div className="mt-4 h-px w-full bg-gold/15">
              <motion.div
                className="h-px bg-gold"
                animate={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${step}-${lang}`}
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -32 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`quote-step-${step + 1}`}
            >
              {steps[step]}
            </motion.div>
          </AnimatePresence>

          <div className="mt-14 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="quote-back-btn"
              className="flex items-center gap-3 font-sans text-[11px] tracking-[0.3em] text-ivory/50 transition-colors duration-300 hover:text-gold disabled:opacity-0"
            >
              <ArrowLeft size={16} strokeWidth={1.25} /> {q.buttons.back}
            </button>
            {step < TOTAL_STEPS - 1 ? (
              <button
                type="button"
                onClick={() => {
                  if (!canContinue()) return;
                  if (step === 0) track("quote_started", { event_type: data.event_type });
                  setStep((s) => s + 1);
                }}
                disabled={!canContinue()}
                data-testid="quote-continue-btn"
                className="flex items-center gap-3 border border-gold px-8 py-4 font-sans text-[11px] tracking-[0.3em] text-gold transition-colors duration-300 hover:bg-gold hover:text-midnight disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gold"
              >
                {q.buttons.continue} <ArrowRight size={16} strokeWidth={1.25} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="bg-gold px-10 py-4 font-sans text-[11px] tracking-[0.3em] text-midnight transition-colors duration-300 hover:bg-gold-light disabled:opacity-50"
              >
                {submitting ? q.buttons.sending : q.buttons.submit}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default QuotePage;
